import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import kebabIcon from "../../assets/img/kebab-horizontal.svg";
import eyeIcon from "../../assets/img/eye-open.svg";
import editPencilIcon from "../../assets/img/edit_pencil_line.svg";
import copyIcon from "../../assets/img/copy.svg";
import trashIcon from "../../assets/img/trash.svg";
import { isCampaignPromo } from "./utils";

// Menu is portaled via container="body" + strategy="fixed" so it escapes the
// sticky first column's stacking context (which would otherwise clip it).
// Modals use a higher z-index, so this does not conflict with modal layering.
const PromoRowActions = ({
  promo,
  isEditable,
  isViewable,
  isDeleted = false,
  onEdit,
  onView,
  onDelete,
}) => {
  const isCampaign = isCampaignPromo(promo);
  // EO-1264: a deleted promo is read-only, so Copy is the only action left — it opens the create
  // form prefilled and saves a new draft, leaving the deleted row untouched. Edit, Delete and
  // View all act on (or open) a row that no longer exists, and the backend rejects them anyway.
  const showEditCopy = isEditable && !isCampaign && !isDeleted;
  const showDelete = isEditable && !isCampaign && !isDeleted;
  const showCopyOnly = isDeleted && isEditable && !isCampaign;
  const showView = isViewable && !isDeleted;
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((prev) => !prev);

  // Nothing to offer for this row. A Campaign promo is never editable here, but it
  // is viewable (EO-1538), so it keeps View.
  if (!showView && !showEditCopy && !showDelete && !showCopyOnly) return null;

  return (
    <Dropdown
      isOpen={open}
      toggle={toggle}
      direction="down"
      className="promo-row-actions"
    >
      <DropdownToggle
        tag="button"
        type="button"
        className="promo-row-actions-toggle"
        aria-label={`More actions for ${promo.name}`}
        data-testid={`promo-row-actions-${promo.id}`}
      >
        <img src={kebabIcon} alt="" width="20" height="20" />
      </DropdownToggle>
      <DropdownMenu
        end
        strategy="fixed"
        container="body"
        className="promo-row-actions-menu"
      >
        {showCopyOnly && (
          <DropdownItem onClick={() => onEdit(promo, true)}>
            <img
              src={copyIcon}
              alt=""
              width="20"
              height="20"
              data-testid="menu-icon-copy"
            />
            <span>Copy</span>
          </DropdownItem>
        )}
        {showView && (
          <DropdownItem onClick={() => (onView ?? onEdit)(promo)}>
            <img
              src={eyeIcon}
              alt=""
              width="20"
              height="20"
              data-testid="menu-icon-view"
            />
            <span>View</span>
          </DropdownItem>
        )}
        {showEditCopy && (
          <>
            <DropdownItem onClick={() => onEdit(promo)}>
              <img
                src={editPencilIcon}
                alt=""
                width="20"
                height="20"
                data-testid="menu-icon-edit"
              />
              <span>Edit</span>
            </DropdownItem>
            <DropdownItem onClick={() => onEdit(promo, true)}>
              <img
                src={copyIcon}
                alt=""
                width="20"
                height="20"
                data-testid="menu-icon-copy"
              />
              <span>Copy</span>
            </DropdownItem>
          </>
        )}
        {showDelete && (
          <DropdownItem
            className="promo-row-actions-delete"
            onClick={() => onDelete(promo)}
          >
            <img
              src={trashIcon}
              alt=""
              width="20"
              height="20"
              data-testid="menu-icon-delete"
            />
            <span>Delete</span>
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

PromoRowActions.propTypes = {
  promo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    promoType: PropTypes.string,
  }).isRequired,
  isEditable: PropTypes.bool,
  isViewable: PropTypes.bool,
  isDeleted: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onView: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
};

export default PromoRowActions;
